/**
 * spaceBgDecorationCatalog.ts
 * Per-kind palette, hashing, and shape recipes for the World 2
 * backdrop decoration layer. Split out of `spaceBgDecorations.ts` so
 * the builder file stays under the AGENTS.md 300-line cap and so the
 * catalog can be unit-tested or re-tuned in isolation.
 *
 * Pure functions / readonly constants only — no state, no React,
 * no rendering. All exports are deterministic given their inputs.
 *
 * @module spaceBgDecorationCatalog
 */

import type {
  SpaceBgDebrisChunk,
  SpaceBgDecorationKind,
} from './spaceBgDecorationTypes';

/** Body palette per kind — base fill picks + lit-limb highlight. */
const PALETTE: Record<
  SpaceBgDecorationKind,
  { base: readonly string[]; highlight: string }
> = {
  // Cool blues / teals — dominant W2 palette.
  'planet-small': {
    base: ['#3a4a78', '#4a5e92', '#33526e', '#406280'] as const,
    highlight: '#9bbed0',
  },
  // Warm muted reds / oranges — visual contrast for ~1 in 4 bodies.
  'planet-medium': {
    base: ['#7a3a4a', '#9a5040', '#88485a', '#a05a3c'] as const,
    highlight: '#e0b09a',
  },
  // Gas-giant feel — wide hue spread, deep saturation.
  'planet-giant': {
    base: ['#5a3a78', '#3a5a72', '#704868', '#3a684c'] as const,
    highlight: '#c8b8e8',
  },
  // Ringed bodies — paler ice / methane.
  'planet-ringed': {
    base: ['#90a4b8', '#a8a890', '#b08898', '#88a8b0'] as const,
    highlight: '#e8e8f0',
  },
  // Tiny dwarf bodies — washed grey.
  'planet-dwarf': {
    base: ['#888899', '#aaa896', '#959abe', '#a8a8a8'] as const,
    highlight: '#d8d8e0',
  },
  // Debris field colour — dusky brown.
  exploded: {
    base: ['#5a4438', '#6a4c40', '#52443a', '#604a40'] as const,
    highlight: '#a8907c',
  },
  // Star twinkles — single off-white tone.
  star: {
    base: ['#eaeaff'] as const,
    highlight: '#ffffff',
  },
};

/** Ring colour palette — paired with `planet-ringed` body picks. */
const RING_PALETTE: readonly string[] = [
  '#a0b8d0',
  '#d0c4a8',
  '#b8a8c0',
  '#c8b890',
] as const;

/**
 * Cheap integer hash of a 32-bit signed input. xorshift-style mix —
 * fast, branchless, and avalanche-good enough that consecutive bucket
 * indices yield uncorrelated outputs. Constants from Murmur3 finaliser.
 *
 * @param x - 32-bit signed input to mix.
 * @returns Non-negative 32-bit mixed output.
 */
export function hash32(x: number): number {
  let h = x | 0;
  h = Math.imul(h ^ (h >>> 16), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  h = (h ^ (h >>> 16)) >>> 0;
  return h;
}

/**
 * Resolve the decoration kind for a given bucket index. Stars dominate
 * so the screen feels like deep space; planets sprinkle in with enough
 * variety that two adjacent buckets rarely render the same kind.
 *
 * @param bucket - 32-bit bucket index along the world X axis.
 * @returns The decoration kind to render in that bucket.
 */
export function kindForBucket(bucket: number): SpaceBgDecorationKind {
  // DECISION: Independent hash from the position hash so kind and Y
  // are uncorrelated — same bucket position is not always the same
  // kind across nearby buckets.
  const r = hash32(bucket ^ 0x9e3779b1) % 100;
  if (r < 5) return 'planet-ringed';
  if (r < 10) return 'exploded';
  if (r < 15) return 'planet-giant';
  if (r < 23) return 'planet-medium';
  if (r < 33) return 'planet-small';
  if (r < 45) return 'planet-dwarf';
  return 'star';
}

/**
 * Body radius (screen px) per kind, modulated by the position hash so
 * adjacent planets of the same kind don't share the exact same size.
 *
 * @param kind - The decoration kind.
 * @param posHash - The bucket position hash (used to perturb the radius).
 * @returns Body radius in screen px (0 for `exploded`).
 */
export function radiusForKind(
  kind: SpaceBgDecorationKind,
  posHash: number,
): number {
  switch (kind) {
    case 'planet-small':
      return 18 + ((posHash >>> 4) % 6);
    case 'planet-medium':
      return 26 + ((posHash >>> 4) % 8);
    case 'planet-giant':
      return 42 + ((posHash >>> 4) % 10);
    case 'planet-ringed':
      return 22 + ((posHash >>> 4) % 6);
    case 'planet-dwarf':
      return 6 + ((posHash >>> 4) % 4);
    case 'star':
      return 1 + ((posHash >>> 4) % 2);
    case 'exploded':
      return 0;
  }
}

/**
 * Pick the body fill colour for a kind / position hash pair.
 *
 * @param kind - The decoration kind.
 * @param posHash - The bucket position hash.
 * @returns Body fill colour as a CSS hex string.
 */
export function colorForKind(
  kind: SpaceBgDecorationKind,
  posHash: number,
): string {
  const palette = PALETTE[kind];
  return palette.base[(posHash >>> 8) % palette.base.length];
}

/**
 * Highlight (lit-limb) colour for a kind. Constant per kind.
 *
 * @param kind - The decoration kind.
 * @returns Highlight colour as a CSS hex string.
 */
export function highlightForKind(kind: SpaceBgDecorationKind): string {
  return PALETTE[kind].highlight;
}

/**
 * Build the ring spec for a `planet-ringed` body. Tilt varies by hash
 * so adjacent ringed bodies don't share a profile.
 *
 * @param posHash - The bucket position hash.
 * @param radius - The body radius.
 * @returns Ring radii + colour ready to drop on a decoration entry.
 */
export function buildRing(
  posHash: number,
  radius: number,
): { radiusX: number; radiusY: number; color: string } {
  const ringW = radius * 2.4;
  const ringH = 2 + ((posHash >>> 12) % 5); // 2..6 px tall ring
  return {
    radiusX: ringW,
    radiusY: ringH,
    color: RING_PALETTE[(posHash >>> 20) % RING_PALETTE.length],
  };
}

/**
 * Build the debris cluster for an `exploded` decoration. 6..10 small
 * chunks scattered in a ring around the centre with stable per-chunk
 * positions.
 *
 * @param bucket - The bucket index (drives per-chunk hash).
 * @param posHash - The bucket position hash (drives chunk count).
 * @returns Debris chunk list.
 */
export function buildDebris(
  bucket: number,
  posHash: number,
): SpaceBgDebrisChunk[] {
  const count = 6 + ((posHash >>> 4) % 5);
  const debris: SpaceBgDebrisChunk[] = [];
  for (let i = 0; i < count; i += 1) {
    const ch = hash32(bucket * 1009 + i);
    const angle = (ch % 360) * (Math.PI / 180);
    const dist = 10 + ((ch >>> 8) % 16);
    debris.push({
      dx: Math.cos(angle) * dist,
      dy: Math.sin(angle) * dist,
      radius: 1 + ((ch >>> 16) % 3),
    });
  }
  return debris;
}
